import { useCallback, useEffect, useRef, useState } from "react"

export type ImmediatePhotoStatus = "uploading" | "putDone" | "error"

export interface ImmediatePhotoItem {
    id: string
    status: ImmediatePhotoStatus
    // プレビュー用 objectURL（item 生存中は保持し、破棄時に revoke する）
    objectURL: string
    blob: Blob
    // PUT 成功時にサーバから受領。complete / create のリクエストに含める。
    uploadId?: string
    // 内部用: キューで起動済みか
    requested: boolean
    // 内部用: 撮影中の × による中断用
    abortController: AbortController
}

interface UseImmediatePhotoUploadParams {
    // 1枚分の issue + S3 PUT を行い uploadId を返す。画面ごとに注入する。
    put: (blob: Blob, signal: AbortSignal) => Promise<{ uploadId: string }>
    // 同時 PUT 数の上限
    throttle?: number
}

const DEFAULT_THROTTLE = 5

/**
 * 撮影/選択と同時に S3 へ PUT（pending prefix）を先行実行する即時アップロード用フック。
 *
 * - `addPhoto(blob)`: プレビューを即表示しつつ PUT をスロットリングで起動
 * - `removePhoto(id)`: PUT 中なら abort、いずれも objectURL を revoke してローカル除外
 * - `getReadyUploadIds()`: PUT 完了済み（putDone）の uploadId 一覧。確定操作で complete/create に渡す
 * - `waitAllSettled()`: 進行中の PUT がすべて putDone / error に落ち着くまで待つ
 * - `reset()`: 全 abort + revoke して空にする
 *
 * complete（確定）は呼び出し側が確定操作時にまとめて送る（deferred-complete）。
 * 確定されなかった pending オブジェクトはバックエンドの S3 ライフサイクルで自動回収される。
 */
export const useImmediatePhotoUpload = ({
    put,
    throttle = DEFAULT_THROTTLE
}: UseImmediatePhotoUploadParams) => {
    const [photos, setPhotos] = useState<ImmediatePhotoItem[]>([])
    const [kick, setKick] = useState(0)
    const uploadingCount = useRef(0)
    const counter = useRef(0)

    // 最新の値を effect / コールバックから参照するための ref
    const putRef = useRef(put)
    const photosRef = useRef<ImmediatePhotoItem[]>([])
    const waitersRef = useRef<(() => void)[]>([])

    // render 中に ref を書き換えると lint(react-hooks/refs) に抵触するため effect で同期する。
    // 後続の PUT キュー effect より前に定義し、参照時に最新値が入っている状態にする。
    useEffect(() => {
        putRef.current = put
        photosRef.current = photos
    })

    const updateItem = useCallback(
        (id: string, patch: Partial<ImmediatePhotoItem>) => {
            setPhotos(prev =>
                prev.map(it => (it.id === id ? { ...it, ...patch } : it))
            )
        },
        []
    )

    const addPhoto = useCallback((blob: Blob) => {
        const id = `${counter.current++}`
        const item: ImmediatePhotoItem = {
            id,
            status: "uploading",
            objectURL: URL.createObjectURL(blob),
            blob,
            requested: false,
            abortController: new AbortController()
        }
        setPhotos(prev => [...prev, item])
        setKick(k => k + 1)
    }, [])

    // error 状態の写真を再 PUT する（新しい uploadId を発行。旧 pending は自動回収される）。
    const retryPhoto = useCallback((id: string) => {
        setPhotos(prev =>
            prev.map(it =>
                it.id === id && it.status === "error"
                    ? {
                          ...it,
                          status: "uploading",
                          requested: false,
                          abortController: new AbortController()
                      }
                    : it
            )
        )
        setKick(k => k + 1)
    }, [])

    const removePhoto = useCallback((id: string) => {
        const target = photosRef.current.find(it => it.id === id)
        if (target) {
            if (target.status === "uploading") {
                target.abortController.abort()
            }
            URL.revokeObjectURL(target.objectURL)
        }
        setPhotos(prev => prev.filter(it => it.id !== id))
    }, [])

    // PUT キュー: 未起動の uploading を throttle 件まで起動する
    useEffect(() => {
        const pending = photosRef.current.filter(
            it => it.status === "uploading" && !it.requested
        )
        if (pending.length === 0) return

        const slots = Math.max(0, throttle - uploadingCount.current)
        const toStart = pending.slice(0, slots)
        if (toStart.length === 0) return

        const startIds = new Set(toStart.map(it => it.id))
        setPhotos(prev =>
            prev.map(it =>
                startIds.has(it.id) ? { ...it, requested: true } : it
            )
        )

        toStart.forEach(item => {
            uploadingCount.current++
            putRef
                .current(item.blob, item.abortController.signal)
                .then(({ uploadId }) => {
                    updateItem(item.id, { status: "putDone", uploadId })
                })
                .catch(error => {
                    // abort は意図的な中断（item は既に除外済み）なので何もしない
                    if (
                        item.abortController.signal.aborted ||
                        (error as { name?: string })?.name === "AbortError"
                    ) {
                        return
                    }
                    updateItem(item.id, { status: "error" })
                })
                .finally(() => {
                    uploadingCount.current = Math.max(
                        0,
                        uploadingCount.current - 1
                    )
                    setKick(k => k + 1)
                })
        })
    }, [kick, throttle, updateItem])

    // 進行中の PUT がなくなったら waitAllSettled の待機者を解放する
    useEffect(() => {
        if (!photos.some(it => it.status === "uploading")) {
            const waiters = waitersRef.current
            waitersRef.current = []
            waiters.forEach(resolve => resolve())
        }
    }, [photos])

    const waitAllSettled = useCallback((): Promise<void> => {
        if (!photosRef.current.some(it => it.status === "uploading")) {
            return Promise.resolve()
        }
        return new Promise<void>(resolve => {
            waitersRef.current.push(resolve)
        })
    }, [])

    const getReadyUploadIds = useCallback(
        (): string[] =>
            photosRef.current
                .filter(it => it.status === "putDone" && it.uploadId)
                .map(it => it.uploadId!),
        []
    )

    // waitAllSettled 後など、レンダー由来の stale な hasError ではなく最新値を見たいとき用。
    const getHasError = useCallback(
        (): boolean => photosRef.current.some(it => it.status === "error"),
        []
    )

    const reset = useCallback(() => {
        photosRef.current.forEach(it => {
            if (it.status === "uploading") {
                it.abortController.abort()
            }
            URL.revokeObjectURL(it.objectURL)
        })
        uploadingCount.current = 0
        setPhotos([])
    }, [])

    // unmount 時に全 objectURL を revoke し、進行中の PUT を abort する
    useEffect(
        () => () => {
            photosRef.current.forEach(it => {
                if (it.status === "uploading") {
                    it.abortController.abort()
                }
                URL.revokeObjectURL(it.objectURL)
            })
        },
        []
    )

    const hasError = photos.some(it => it.status === "error")
    const isUploading = photos.some(it => it.status === "uploading")
    const allPutDone =
        photos.length > 0 && photos.every(it => it.status === "putDone")

    return {
        photos,
        addPhoto,
        removePhoto,
        retryPhoto,
        getReadyUploadIds,
        getHasError,
        waitAllSettled,
        reset,
        hasError,
        isUploading,
        allPutDone,
        count: photos.length
    }
}
